import React, { useState, useEffect, useCallback } from 'react';
import './ScrollToTop.css';

const WHATSAPP_PHONE = '5215515957155';

/** Texto base que siempre se incluye al abrir WhatsApp (se combina con nombre, empresa y mensaje adicional). */
const DEFAULT_WHATSAPP_MESSAGE =
  'Me interesa conocer más sobre los servicios de automatización de TERA. ¿Podrían ayudarme con más información?';

function buildWhatsAppText(name: string, company: string, extraMessage: string): string {
  const n = name.trim();
  const c = company.trim();
  const extra = extraMessage.trim();
  const lines: string[] = [];
  if (n && c) {
    lines.push(`Hola, soy ${n} de ${c}.`);
  } else if (n) {
    lines.push(`Hola, soy ${n}.`);
  } else if (c) {
    lines.push(`Hola, les escribo desde ${c}.`);
  } else {
    lines.push('Hola.');
  }
  lines.push('');
  lines.push(DEFAULT_WHATSAPP_MESSAGE);
  if (extra) {
    lines.push('');
    lines.push(extra);
  }
  return lines.join('\n');
}

function buildWhatsAppUrl(name: string, company: string, extraMessage: string): string {
  const text = buildWhatsAppText(name, company, extraMessage);
  return `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(text)}`;
}

const ScrollToTop: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [waName, setWaName] = useState('');
  const [waCompany, setWaCompany] = useState('');
  const [waMessage, setWaMessage] = useState('');

  // Mostrar/ocultar el botón basado en la posición del scroll
  useEffect(() => {
    const toggleVisibility = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      if (scrollTop > 200) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    // Ejecutar inmediatamente para verificar posición actual
    toggleVisibility();
    
    window.addEventListener('scroll', toggleVisibility, { passive: true });
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  useEffect(() => {
    if (!whatsappOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setWhatsappOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [whatsappOpen]);

  // Función para hacer scroll hacia arriba
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const openWhatsAppModal = () => {
    setWaName('');
    setWaCompany('');
    setWaMessage('');
    setWhatsappOpen(true);
  };

  const closeWhatsAppModal = useCallback(() => {
    setWhatsappOpen(false);
  }, []);

  const confirmWhatsApp = () => {
    const url = buildWhatsAppUrl(waName, waCompany, waMessage);
    window.open(url, '_blank', 'noopener,noreferrer');
    setWhatsappOpen(false);
  };

  const handleWhatsAppFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    confirmWhatsApp();
  };

  return (
    <>
      {whatsappOpen && (
        <div
          className="whatsapp-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="whatsapp-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeWhatsAppModal();
          }}
        >
          <div className="whatsapp-modal">
            <h2 id="whatsapp-modal-title" className="whatsapp-modal-title">
              Contacto por WhatsApp
            </h2>
            <form className="whatsapp-modal-form" onSubmit={handleWhatsAppFormSubmit}>
              <div className="whatsapp-modal-fields">
                <label className="whatsapp-modal-label">
                  Nombre
                  <input
                    type="text"
                    value={waName}
                    onChange={(e) => setWaName(e.target.value)}
                    placeholder="Tu nombre"
                    className="whatsapp-modal-input"
                    autoComplete="name"
                  />
                </label>
                <label className="whatsapp-modal-label">
                  Empresa
                  <input
                    type="text"
                    value={waCompany}
                    onChange={(e) => setWaCompany(e.target.value)}
                    placeholder="Nombre de tu empresa"
                    className="whatsapp-modal-input"
                    autoComplete="organization"
                  />
                </label>
                <label className="whatsapp-modal-label">
                  Mensaje adicional
                  <textarea
                    value={waMessage}
                    onChange={(e) => setWaMessage(e.target.value)}
                    className="whatsapp-modal-textarea"
                    rows={4}
                    placeholder="Ej. detalle de tu proyecto o consulta concreta"
                    required
                  />
                </label>
              </div>
              <div className="whatsapp-modal-actions">
                <button type="submit" className="whatsapp-modal-btn primary">
                  Abrir WhatsApp
                </button>
              </div>
            </form>
            <button
              type="button"
              className="whatsapp-modal-close"
              onClick={(e) => {
                e.stopPropagation();
                closeWhatsAppModal();
              }}
              aria-label="Cerrar"
              title="Cerrar"
            >
              <svg
                className="whatsapp-modal-close-icon"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="floating-buttons">
      <button
        className={`whatsapp-button ${isVisible ? 'visible' : ''}`}
        onClick={openWhatsAppModal}
        aria-label="Contactar por WhatsApp"
        title="Contactar por WhatsApp"
      >
        <div className="whatsapp-button-inner">
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="currentColor"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
          </svg>
        </div>
        <div className="whatsapp-button-ripple"></div>
      </button>
      
      <button
        className={`scroll-to-top ${isVisible ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Volver arriba"
        title="Volver arriba"
      >
        <div className="scroll-to-top-inner">
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M18 15l-6-6-6 6"/>
          </svg>
        </div>
        <div className="scroll-to-top-ripple"></div>
      </button>
      </div>
    </>
  );
};

export default ScrollToTop;